import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import BrandHeader from '../components/BrandHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Payment success → show a two-button launchpad:
 *   1. Open Stripe setup (new tab)
 *   2. Read the setup guide (new tab)
 *
 * We deliberately do NOT auto-redirect, because clubs need both tabs open
 * at once (Stripe asks questions, the guide explains them).
 */
export default function OnboardSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const onboardId = searchParams.get('onboard_id');
  const isPerEntry = !!onboardId && !sessionId;
  const [state, setState] = useState('verifying'); // verifying | paid | pending | error
  const [clubName, setClubName] = useState('');
  const [stripeUrl, setStripeUrl] = useState('');
  const [error, setError] = useState('');
  const [linkingOauth, setLinkingOauth] = useState(false);
  const [oauthError, setOauthError] = useState('');

  useEffect(() => {
    if (!sessionId && !onboardId) {
      setState('error');
      setError('Missing payment reference.');
      return;
    }
    const params = sessionId ? { session_id: sessionId } : { onboard_id: onboardId };
    axios.get(`${API}/onboard/verify`, { params })
      .then(r => {
        setClubName(r.data.club_name || '');
        if (r.data.status === 'paid' || r.data.status === 'ready') {
          setStripeUrl(r.data.stripe_onboarding_url || '');
          setState('paid');
        } else {
          setState(r.data.status || 'pending');
        }
      })
      .catch(err => {
        setState('error');
        setError(err.response?.data?.detail || 'Could not verify your activation. Please contact support.');
      });
  }, [sessionId, onboardId]);

  const connectExistingStripe = async () => {
    setOauthError('');
    setLinkingOauth(true);
    try {
      const r = await axios.post(`${API}/onboard/stripe-oauth-url`, sessionId ? { session_id: sessionId } : { onboard_id: onboardId });
      if (r.data.oauth_url) {
        window.location.href = r.data.oauth_url;
      } else {
        setOauthError('Could not generate a Stripe link. Please try again.');
        setLinkingOauth(false);
      }
    } catch (err) {
      setOauthError(err.response?.data?.detail || 'Could not connect to Stripe.');
      setLinkingOauth(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <BrandHeader />

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-2xl w-full">
          {state === 'verifying' && (
            <div className="text-center" data-testid="verifying-state">
              <div className="w-12 h-12 border-4 border-[#1D3557]/20 border-t-[#1D3557] rounded-full animate-spin mx-auto mb-6" />
              <h1 className="text-2xl font-black text-[#1D3557] mb-2">{isPerEntry ? 'Setting up your club…' : 'Confirming your payment…'}</h1>
              <p className="text-gray-600">One moment while we set up your club.</p>
            </div>
          )}

          {state === 'paid' && (
            <div data-testid="paid-state">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <svg width="32" height="32" fill="none" stroke="#16a34a" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                </div>
                <h1 className="text-3xl sm:text-4xl font-black text-[#1D3557] mb-2">You're in!</h1>
                <p className="text-gray-600 max-w-md mx-auto">
                  {clubName ? <>We've set up <strong>{clubName}</strong>. </> : ''}
                  {isPerEntry
                    ? 'No upfront payment — one more step, link your bank account with Stripe.'
                    : 'One more step — link your bank account with Stripe.'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {stripeUrl ? (
                  <a
                    href={stripeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 py-4 px-5 bg-[#635BFF] hover:bg-[#4F46E5] text-white font-bold rounded-lg transition-colors"
                    data-testid="stripe-connect-link"
                  >
                    Continue to Stripe
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7-7 7M5 12h16" /></svg>
                  </a>
                ) : (
                  <div className="flex items-center justify-center py-4 px-5 bg-gray-100 text-gray-500 font-medium rounded-lg text-sm">
                    Preparing Stripe link…
                  </div>
                )}
                <a
                  href="/help/stripe-setup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-4 px-5 border-2 border-[#1D3557] text-[#1D3557] font-bold rounded-lg hover:bg-[#1D3557] hover:text-white transition-colors"
                  data-testid="setup-guide-link"
                >
                  Open the setup guide
                </a>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900" data-testid="two-tab-tip">
                <strong>Tip:</strong> open both in separate tabs — the guide explains each Stripe question as you go.
                The Stripe flow takes about <strong>10 minutes</strong> and is aimed at businesses, so a few fields will feel unfamiliar. The guide translates them into plain English for clubs.
              </div>

              {/* OAuth: club already has Stripe */}
              <div className="mt-6 pt-6 border-t border-gray-200" data-testid="existing-stripe-panel">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500 mb-2">Already have Stripe?</p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-gray-700">
                    If your club already runs a Stripe account (not a personal one), link it in 20&nbsp;seconds instead.
                  </p>
                  <button
                    onClick={connectExistingStripe}
                    disabled={linkingOauth}
                    className="flex-shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 border-2 border-[#635BFF] text-[#635BFF] hover:bg-[#635BFF] hover:text-white font-bold rounded-lg text-sm transition-colors disabled:opacity-60"
                    data-testid="oauth-existing-stripe-btn"
                  >
                    {linkingOauth ? 'Opening Stripe…' : 'Connect existing Stripe'}
                  </button>
                </div>
                {oauthError && (
                  <p className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700" data-testid="oauth-error">
                    {oauthError}
                  </p>
                )}
              </div>

              <p className="text-xs text-gray-500 mt-6 text-center">
                Once Stripe approves you, your predictor site goes live automatically and we'll email you a one-click dashboard link.
              </p>
            </div>
          )}

          {state === 'pending' && (
            <div className="text-center" data-testid="pending-state">
              <h1 className="text-2xl font-bold text-[#1D3557] mb-3">Payment still processing</h1>
              <p className="text-gray-600">Please wait a moment and refresh this page. If it persists, contact support.</p>
            </div>
          )}

          {state === 'error' && (
            <div className="text-center" data-testid="error-state">
              <h1 className="text-2xl font-bold text-[#1D3557] mb-3">Something went wrong</h1>
              <p className="text-gray-600 mb-4">{error}</p>
              <a href="mailto:support@targetfunder.com" className="inline-block px-6 py-2.5 bg-[#1D3557] text-white font-bold rounded-lg">Contact support</a>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
